import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Send, Heart, ExternalLink, Sparkles, MessageCircle, Instagram, Facebook, Youtube } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

// Public landing, does NOT use the authenticated `api` client (no cookies),
// just plain axios pointed at REACT_APP_BACKEND_URL. Same-origin via backendBase.
const BASE = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BASE}/api`, withCredentials: false, timeout: 15000 });

const PHONE_MASK = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cemedeiros.biblia";
const INSTAGRAM = "https://www.instagram.com/ccarlosmedeiross/";
const FACEBOOK = "https://www.facebook.com/ccarlosmedeiross/";
const YOUTUBE = "https://www.youtube.com/@conectadospelapalavra";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[hsl(var(--parchment))]" data-testid="landing-page">
      <Header />
      <Hero />
      <main className="max-w-4xl mx-auto section-padding pb-20 space-y-16">
        <SignupSection />
        <PrayerSection />
        <WallSection />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="bg-navy text-white border-b border-white/5">
      <div className="max-w-5xl mx-auto section-padding py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/icon-192.png" alt="C E Medeiros" className="w-9 h-9 rounded-xl" />
          <div className="min-w-0">
            <p className="font-display text-base sm:text-lg truncate">C E Medeiros</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/60">Conheça o poder da palavra</p>
          </div>
        </div>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="landing-download-app"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-navy hover:bg-gold/90 text-[11px] uppercase tracking-widest transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Baixar app
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="bg-navy text-white relative overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden>
        <div className="absolute -top-32 -right-20 w-[400px] h-[400px] bg-gold rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-gold/40 rounded-full blur-3xl" />
      </div>
      <div className="max-w-4xl mx-auto section-padding py-16 sm:py-24 relative">
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Ministério Casa de Medeiros</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Caminhe junto<br className="hidden sm:block" />
          <em className="text-gold not-italic">na palavra</em>, todos os dias.
        </h1>
        <p className="mt-6 text-white/75 max-w-xl text-lg leading-relaxed">
          Receba o devocional diário, faça parte do mural de orações da nossa
          comunidade e seja edificado na fé. Cadastro gratuito.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="#cadastro"
            data-testid="landing-cta-signup"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-navy hover:bg-white text-[12px] uppercase tracking-widest font-medium transition-colors"
          >
            <Heart className="w-4 h-4" />
            Quero fazer parte
          </a>
          <a
            href="#oracao"
            data-testid="landing-cta-prayer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white hover:bg-white/10 text-[12px] uppercase tracking-widest transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Pedir oração
          </a>
        </div>
      </div>
    </section>
  );
}

function SignupSection() {
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const update = (k) => (e) => {
    const val = k === "whatsapp" ? PHONE_MASK(e.target.value) : e.target.value;
    if (k === "whatsapp") setPhoneError("");
    setForm((f) => ({ ...f, [k]: val }));
  };

  const validatePhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length < 10) return "Informe DDD + número (mínimo 10 dígitos).";
    if (digits.length > 11) return "Número longo demais. Use apenas DDD + número.";
    if (digits.length === 11 && digits[2] !== "9") {
      return "Celular brasileiro com 11 dígitos deve começar com 9 após o DDD.";
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim()) {
      toast.error("Como podemos te chamar?");
      return;
    }
    const phoneErr = validatePhone(form.whatsapp);
    if (phoneErr) {
      setPhoneError(phoneErr);
      toast.error(phoneErr);
      return;
    }
    setLoading(true);
    try {
      // Backend ainda espera campo `phone`, mapeamos `whatsapp` → `phone`.
      await pub.post("/landing/signup", {
        name: form.name,
        phone: form.whatsapp,
        email: form.email || `sem-email+${Date.now()}@cmedeiros.app`,
        city: form.city,
      });
      setDone(true);
      toast.success("Cadastro recebido!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível cadastrar agora.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cadastro" data-testid="landing-signup-section" className="scroll-mt-20">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Cadastro</p>
      <h2 className="font-display text-3xl sm:text-4xl text-navy mb-3">Quero receber a Palavra do dia</h2>
      <p className="text-navy/65 mb-8 max-w-xl">
        O pastor Carlos envia uma palavra de fé toda manhã às 07h00 direto no seu WhatsApp. Seus dados ficam protegidos.
      </p>

      {done ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 max-w-xl" data-testid="landing-signup-success">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-display text-xl text-emerald-900">Cadastro recebido!</p>
              <p className="text-sm text-emerald-800 mt-2">
                Em alguns instantes você recebe uma mensagem de confirmação aqui no WhatsApp.
                Responda <strong>SIM</strong> para ativar o envio diário, ou <strong>PARE</strong> para cancelar a qualquer momento.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-6 sm:p-7 max-w-xl space-y-4" data-testid="landing-signup-form">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-navy/60">Como podemos te chamar?</label>
            <Input
              value={form.name}
              onChange={update("name")}
              data-testid="landing-input-name"
              placeholder="Seu primeiro nome"
              maxLength={80}
              required
              className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-navy/60">
              WhatsApp <span className="text-rose-600">*</span>
            </label>
            <Input
              value={form.whatsapp}
              onChange={update("whatsapp")}
              data-testid="landing-input-phone"
              placeholder="(81) 99999-9999"
              required
              inputMode="tel"
              className={`mt-2 h-11 bg-[hsl(var(--parchment))] focus:bg-white ${phoneError ? "border-rose-500" : "border-transparent"}`}
            />
            {phoneError ? (
              <p className="text-[11px] text-rose-600 mt-1.5">{phoneError}</p>
            ) : (
              <p className="text-[11px] text-navy/50 mt-1.5">Use o número que recebe WhatsApp. É por aqui que vamos te enviar a Palavra.</p>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-navy/60">
                Email <span className="text-navy/40">(opcional)</span>
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={update("email")}
                data-testid="landing-input-email"
                placeholder="seu@email.com"
                className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-navy/60">
                Cidade <span className="text-navy/40">(opcional)</span>
              </label>
              <Input
                value={form.city}
                onChange={update("city")}
                data-testid="landing-input-city"
                placeholder="Recife, São Paulo…"
                className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="landing-signup-submit"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-navy hover:bg-gold hover:text-navy text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            Receber a Palavra do dia
          </button>
          <p className="text-[10px] text-navy/45 text-center pt-1">
            Ao cadastrar você concorda em receber mensagens no seu WhatsApp. Pode cancelar a qualquer momento enviando PARE.
          </p>
        </form>
      )}
    </section>
  );
}

function PrayerSection() {
  const [form, setForm] = useState({ name: "", phone: "", request: "", is_public: true });
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const validatePhone = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    if (d.length < 10) return "Informe DDD + número (mínimo 10 dígitos).";
    if (d.length === 11 && d[2] !== "9") return "Celular com 11 dígitos deve começar com 9.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const text = form.request.trim();
    if (text.length < 4) {
      toast.error("Compartilhe um pouco mais sobre seu pedido.");
      return;
    }
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) { setPhoneError(phoneErr); toast.error(phoneErr); return; }
    setLoading(true);
    try {
      await pub.post("/landing/prayer", form);
      setDone(true);
      setForm({ name: "", phone: "", request: "", is_public: true });
      toast.success("Pedido enviado. Estaremos orando por você.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar agora.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="oracao" data-testid="landing-prayer-section" className="scroll-mt-20">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Pedido de oração</p>
      <h2 className="font-display text-3xl sm:text-4xl text-navy mb-3">Vamos orar com você</h2>
      <p className="text-navy/65 mb-8 max-w-xl">
        Conte com a nossa intercessão. Sua palavra será lida com cuidado pela equipe pastoral.
      </p>

      {done && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 max-w-xl mb-4" data-testid="landing-prayer-success">
          <p className="text-sm text-emerald-900">
            Recebemos seu pedido. Nossa equipe estará intercedendo por você.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-6 sm:p-7 max-w-xl space-y-4" data-testid="landing-prayer-form">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-navy/60">Nome <span className="text-navy/40">(opcional)</span></label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="landing-prayer-name"
            placeholder="Deixe em branco para Anônimo"
            maxLength={80}
            className="mt-2 h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-navy/60">
            WhatsApp <span className="text-rose-600">*</span>
          </label>
          <Input
            value={form.phone}
            onChange={(e) => { setPhoneError(""); setForm((f) => ({ ...f, phone: PHONE_MASK(e.target.value) })); }}
            data-testid="landing-prayer-phone"
            placeholder="(81) 99999-9999"
            inputMode="tel"
            required
            className={`mt-2 h-11 bg-[hsl(var(--parchment))] focus:bg-white ${phoneError ? "border-rose-500" : "border-transparent"}`}
          />
          {phoneError ? (
            <p className="text-[11px] text-rose-600 mt-1.5">{phoneError}</p>
          ) : (
            <p className="text-[11px] text-navy/50 mt-1.5">A equipe pastoral entrará em contato pelo seu WhatsApp para orar com você.</p>
          )}
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-navy/60">
            Seu pedido <span className="text-navy/40">({form.request.length}/300)</span>
          </label>
          <Textarea
            rows={4}
            value={form.request}
            onChange={(e) => setForm((f) => ({ ...f, request: e.target.value.slice(0, 300) }))}
            data-testid="landing-prayer-request"
            placeholder="Conte o que está no seu coração…"
            maxLength={300}
            className="mt-2 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-navy/75 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_public}
            onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
            data-testid="landing-prayer-public"
            className="w-4 h-4 accent-gold"
          />
          Exibir no mural anonimamente para que outros orem por mim
        </label>
        <button
          type="submit"
          disabled={loading}
          data-testid="landing-prayer-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-gold hover:text-navy text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar pedido
        </button>
      </form>
    </section>
  );
}

function WallSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await pub.get("/landing/wall");
      setItems(r.data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section id="mural" data-testid="landing-wall-section" className="scroll-mt-20">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Mural de orações</p>
      <h2 className="font-display text-3xl sm:text-4xl text-navy mb-3">Pedidos da comunidade</h2>
      <p className="text-navy/65 mb-8 max-w-xl">
        Cada pedido aqui é uma alma que precisa da nossa fé. Pare por um momento e ore.
      </p>

      {loading ? (
        <p className="text-navy/55"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</p>
      ) : items.length === 0 ? (
        <p className="text-navy/55 text-sm" data-testid="landing-wall-empty">
          Ainda não há pedidos no mural. Seja o primeiro a compartilhar.
        </p>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4" data-testid="landing-wall-list">
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`landing-wall-item-${p.id}`}
              className="bg-white border border-border rounded-2xl p-5 hover:border-gold/60 transition-colors"
            >
              <div className="flex items-start gap-2 mb-3">
                <Heart className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">{p.name}</p>
              </div>
              <p className="text-sm text-navy/85 leading-relaxed whitespace-pre-line">{p.request}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-navy text-white/70 border-t border-white/5">
      <div className="max-w-5xl mx-auto section-padding py-10 grid sm:grid-cols-2 gap-6 items-center">
        <div>
          <p className="font-display text-xl text-white">C E Medeiros</p>
          <p className="text-[11px] mt-1">Casa de Medeiros · cemedeiros.com.br</p>
        </div>
        <div className="flex sm:justify-end items-center gap-3">
          <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="landing-social-instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center transition-colors" aria-label="Instagram">
            <Instagram className="w-4 h-4" />
          </a>
          <a href={FACEBOOK} target="_blank" rel="noreferrer" data-testid="landing-social-facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center transition-colors" aria-label="Facebook">
            <Facebook className="w-4 h-4" />
          </a>
          <a href={YOUTUBE} target="_blank" rel="noreferrer" data-testid="landing-social-youtube" className="w-9 h-9 rounded-full bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center transition-colors" aria-label="YouTube">
            <Youtube className="w-4 h-4" />
          </a>
        </div>
      </div>
    </footer>
  );
}
